/*---------------------------------------------------------------------------------------------
 *  Copyright (c) by Solusek Systems. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { qreq } from "../../shared/qrequest";
import AppActions from "../AppActions";
import L from "../Lang"
import BaseCom from "../BaseCom";
import Card from "../Card";
import FormInput from "../FormInput";
import Icon from "../Icon";
import { ProcessingModalGlobal } from "../ProcessingModal";
import { withParamsAndNavigate } from "../WithParamsAndNavigate";

class ProductLineEdit extends BaseCom {
    constructor(props) {
        super(props);
        this.state = {
            item: { panels: [] },
            example: {
                name: 'Product Line Name', code: 'PLN'
            }
        }
    }

    async componentDidMount() {

        if (this.props.params?.id && this.props.params.id !== 'new') {
            await this.loadAsync(this.props.params.id);
        }

        await this.fillListAsync('/api/report-template/list', 'templates');
    }

    loadAsync = async (id) => {
        try {
            let j = await qreq.getAsync('/api/productline/get/' + id);
            if (j.errorCode === 0) {
                this.setState({ item: { ...j.item, panels: (j.item.panels ?? []).map(i => ({ ...i, selected: true })) } });
            }
            else
                this.alert(j.errorMessage);
        }
        catch {
            this.unkownErrorCallback();
        }
    }

    change = () => {
        this.setState({ item: { ...this.state.item } });
    }

    submit = async (e) => {
        e?.preventDefault();
        if (this.state.saving || this.state.suggesting) return;
        this.setState({ saving: true });
        try {
            var j = await qreq.postAsync('/api/productline/save', this.state.item);
            if (j.errorCode === 0) {
                this.success('Product line saved');
                this.goBack();
            }
            else
                this.alert(j.errorMessage || 'Error saving product line');
        }
        catch (e) {
            this.alert(e);
        }
        finally {
            this.setState({ saving: false });
        }
    }

    suggest = async () => {
        if (this.state.suggesting || this.state.saving || !this.state.item.name?.trim()) return;
        this.setState({ suggesting: true });
        ProcessingModalGlobal.show();
        try {
            let j = await qreq.taskAsync('/api/productline/suggest', this.state.item);
            if (j.errorCode === 0) {
                this.setState({ item: j.item });
            }
            else
                this.alert(j.errorMessage);
        }
        catch (e) {
            this.unkownErrorCallback();
        }
        finally {
            ProcessingModalGlobal.hide();
            this.setState({ suggesting: false });
        }
    }

    togglePanel = (e, item) => {
        const selected = e.target.checked;
        this.setState(state => ({ item: { ...state.item, panels: (state.item.panels ?? []).map(i => i === item ? { ...i, selected } : i) } }));
    }

    toggleAllPanels = (e) => {
        const selected = e.target.checked;
        this.setState(state => ({ item: { ...state.item, panels: (state.item.panels ?? []).map(i => ({ ...i, selected })) } }));
    }

    isAllPanelsSelected = () => {
        return this.state.item.panels?.length > 0 && this.state.item.panels.every(i => i.selected);
    }

    render() {
        return <div className="container-fluid u-page">
            <form onSubmit={this.submit}>
                <header className="u-header">
                    <h1><L>{this.props.params?.id && this.props.params.id !== 'new' ? 'Edit Product Line' : 'New Product Line'}</L></h1>
                    <p className="u-intro"><L>Organize related panels into a laboratory product line.</L></p>
                </header>
                <Card title="Basic Information">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <FormInput model={this.state.item} name="name" label="Name" required placeholder={this.state.example.name} onChange={this.change} />
                        </div>
                        <div className="col-md-6">
                            <FormInput model={this.state.item} name="code" label="Code" required placeholder={this.state.example.code} onChange={this.change} />
                        </div>
                    </div>
                </Card>
                <Card title="Reporting">
                    <FormInput model={this.state.item} name="reportTemplateID" label="Report Template" type="select" options={this.state.templates || []} required onChange={this.change} />
                </Card>
                {this.state.item.panels && <Card title="Panels">
                    <button onClick={this.suggest} type="button" className="btn btn-success mb-3" disabled={!this.state.item.name?.trim() || this.state.suggesting || this.state.saving}><Icon icon="faWandMagicSparkles" /> <L>Suggest Panels</L></button>
                    <p className="text-body-secondary small"><L>Review generated panels before saving.</L></p>
                    <div className="small mb-2" role="status">{(this.state.item.panels ?? []).filter(i => i.selected).length} / {(this.state.item.panels ?? []).length} <L>panels selected</L></div>
                    <div className="table-responsive"><table className="table table-hover align-middle">
                        <thead>
                            <tr><th><input type="checkbox" aria-label="Select all panels" checked={this.isAllPanelsSelected()} onChange={this.toggleAllPanels} /></th><th><L>Name</L></th><th><L>Code</L></th></tr>
                        </thead>
                        <tbody>
                            {this.state.item.panels?.map((i, idx) =>
                                <tr key={idx}><td><input type="checkbox" aria-label={`Select ${i.name}`} checked={!!i.selected} onChange={e => this.togglePanel(e, i)} /></td><td>{i.name}</td><td>{i.code}</td></tr>
                            )}
                        </tbody>
                    </table></div>
                </Card>}
                <Card title="Settings">
                    <div className="row g-3">
                        <div className="col-md-4">
                            <FormInput model={this.state.item} name="isDisabled" label="Disable" type="switch" onChange={this.change} />
                            <small className="text-body-secondary"><L>Disable the product line completely from use.</L></small>
                        </div>
                    </div>
                </Card>
                <AppActions>
                    <button type="submit" className="btn btn-success btn-lg" disabled={this.state.saving || this.state.suggesting}><Icon icon="faSave" /> <L>{this.state.saving ? 'Saving...' : 'Save'}</L></button>
                </AppActions>
            </form>
        </div>;
    }
}

export default withParamsAndNavigate(ProductLineEdit);